// Firmware operations are exposed by oc-admin-svc. This remains distinct from
// the SSO Admin Users API configured through REACT_APP_ADMIN_APPSYNC_URL.
import { getAppConfig } from '../config/runtimeConfig';

export const FIRMWARE_REGIONS = ['EU_CENTRAL_1', 'US_EAST_1'];
export const FIRMWARE_UPLOAD_REGIONS = ['ALL', ...FIRMWARE_REGIONS];
export const FIRMWARE_CHANNELS = ['INTERNAL', 'ALPHA', 'BETA', 'PRODUCTION'];
export const AVAILABILITY_GROUP_TYPES = FIRMWARE_CHANNELS;
export const RELEASE_NOTE_LANGUAGES = ['en', 'it', 'pl', 'pt', 'de'];

const FIRMWARE_FIELDS = `
  id
  deviceModel
  hardwareVersion
  firmwareVersion
  checksum
  sizeInBytes
  requiredFirmwareVersion
  createdAt
  updatedAt
`;

const FIRMWARE_LIST_FIELDS = `
  checksum
  createdAt
  deviceModel
  firmwareVersion
  hardwareVersion
  id
  releaseDate
  requiredFirmwareVersion
  sbomKeys
  sizeInBytes
  updatedAt
`;

const ERROR_INFO_FIELDS = `
  error
  errorCode
  message
`;

const AVAILABLE_FIRMWARE_FIELDS = `
  id
  update
  version
  releaseDate
  promotions { id firmwareId toChannel createdAt promotedBy }
`;

const RELEASE_NOTE_FIELDS = `
  body
  createdAt
  firmwareId
  id
  language
  title
  updatedAt
`;

const CHANGE_LOG_FIELDS = `
  body
  createdAt
  firmwareId
  id
  language
  title
  updatedAt
`;

const AVAILABILITY_GROUP_FIELDS = `
  id
  groupName
  groupType
  createdAt
  updatedAt
`;

const AVAILABILITY_GROUP_DETAIL_FIELDS = `
  ${AVAILABILITY_GROUP_FIELDS}
  firmwareList
  userList { userId userRegion }
`;

const DEVICE_SHADOW_FIELDS = `
  deviceId
  childDeviceId
  deviceName
  deviceType
  connectionStatus
  firmwareVersion
  hardwareVersion
  updatedAt
  manufacturer {
    model
    serialNumber
  }
  interface {
    model
    serialNumber
    hardwareVersion
    firmwareVersion
  }
  location {
    country
    timezone {
      id
    }
  }
`;

async function graphqlRequest(query, variables, idToken) {
  const appsyncUrl = getAppConfig('REACT_APP_FIRMWARE_APPSYNC_URL').replace(/\/+$/, '');
  if (!idToken) {
    throw new Error('Missing ID token. Please sign in again.');
  }
  if (!appsyncUrl) {
    throw new Error('REACT_APP_FIRMWARE_APPSYNC_URL is not configured.');
  }

  const response = await fetch(appsyncUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result?.message || `Admin API request failed (${response.status}).`);
  }
  if (result.errors?.length) {
    throw new Error(result.errors[0].message || 'Admin API request failed.');
  }

  return result.data;
}

function throwRegionalError(result, operation) {
  const errorInfo = result?.errorInfo;
  if (!errorInfo) return;
  throw new Error(errorInfo.message || errorInfo.error || `${operation} failed.`);
}

function firmwareManagementResult(results, operation) {
  const result = Array.isArray(results) ? results[0] : null;
  if (!result) throw new Error(`No ${operation} response was returned.`);
  throwRegionalError(result, operation);
  return result;
}

function availableFirmwareResults(results, operation) {
  if (!Array.isArray(results)) return [];

  return results.flatMap((result) => {
    throwRegionalError(result, operation);
    if (!result?.firmwareData) return [];
    return [{ region: result.region, ...result.firmwareData }];
  });
}

function parsePresignedPost(serializedPost) {
  let post = serializedPost;
  if (typeof post === 'string') {
    try {
      post = JSON.parse(post);
    } catch {
      throw new Error('The upload API returned an invalid presigned upload payload.');
    }
  }

  // fileNames returns a batch of presigned posts, even for one firmware file.
  if (!Array.isArray(post) || post.length !== 1) {
    throw new Error('The upload API did not return exactly one firmware upload destination.');
  }
  [post] = post;
  if (!post?.url || !post?.fields || !post.fields.key) {
    throw new Error('The upload API did not return the required S3 upload fields.');
  }

  return post;
}

async function uploadFileToS3(file, post) {
  const formData = new FormData();
  formData.append('Content-Type', file.type || 'application/octet-stream');

  Object.entries(post.fields).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  formData.append('file', file, file.name);

  const response = await fetch(post.url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Firmware file upload failed (${response.status}).`);
  }
}

export async function uploadFirmware(idToken, { file, deviceModel, hardwareVersion, firmwareVersion, requiredFirmwareVersion }) {
  const uploadUrlQuery = `query UploadFirmwareFile($fileNames: [String!]) {
    uploadURL(fileNames: $fileNames) {
      url
      errorInfo { ${ERROR_INFO_FIELDS} }
    }
  }`;
  const uploadUrlData = await graphqlRequest(uploadUrlQuery, { fileNames: [file.name] }, idToken);
  const upload = firmwareManagementResult(uploadUrlData?.uploadURL, 'Firmware upload URL request');
  const post = parsePresignedPost(upload?.url);

  await uploadFileToS3(file, post);

  const registerMutation = `mutation RegisterFirmware($input: RequestFirmwareUploadInput!) {
    requestFirmwareUpload(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmware { ${FIRMWARE_FIELDS} }
    }
  }`;
  const registeredData = await graphqlRequest(registerMutation, {
    input: {
      key: post.fields.key,
      deviceModel,
      hardwareVersion,
      firmwareVersion,
      requiredFirmwareVersion: requiredFirmwareVersion || '',
    },
  }, idToken);

  const registeredFirmware = firmwareManagementResult(
    registeredData?.requestFirmwareUpload,
    'Firmware registration',
  );
  if (!registeredFirmware.firmware?.id) {
    throw new Error('Firmware registration completed without a firmware record.');
  }
  return withFirmwarePromotions(idToken, { region: registeredFirmware.region, ...registeredFirmware.firmware });
}

async function withFirmwarePromotions(idToken, firmware) {
  try {
    return { ...firmware, promotions: await listPromotions(idToken, { firmwareId: firmware.id }) };
  } catch (error) {
    // An unavailable channel lookup must not turn an accepted upload into a retry.
    return { ...firmware, promotionsError: error instanceof Error ? error.message : String(error) };
  }
}

export async function checkAvailableFirmware(idToken, { regionId, deviceModel, hardwareVersion, currentVersion }) {
  const query = `query CheckAvailableFirmware(
    $regionId: RegionName!
    $deviceModel: String!
    $hardwareVersion: String!
    $currentVersion: String!
  ) {
    checkAvailableFirmware(
      regionId: $regionId
      deviceModel: $deviceModel
      hardwareVersion: $hardwareVersion
      currentVersion: $currentVersion
    ) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmwareData { ${AVAILABLE_FIRMWARE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { regionId, deviceModel, hardwareVersion, currentVersion }, idToken);
  return availableFirmwareResults(data?.checkAvailableFirmware, 'Firmware compatibility check');
}

export async function listFirmwares(idToken, { limit = 10, nextToken = '', includePromotions = true } = {}) {
  const query = `query ListFirmwares($limit: Int, $nextToken: String) {
    listFirmwares(limit: $limit, nextToken: $nextToken) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmware {
        items { ${FIRMWARE_LIST_FIELDS} }
        nextToken
      }
    }
  }`;

  const data = await graphqlRequest(query, { limit, nextToken }, idToken);
  const result = firmwareManagementResult(data?.listFirmwares, 'Firmware package list');
  const connection = result.firmware;

  const items = (connection?.items || []).filter(Boolean).map((firmware) => ({ region: result.region, ...firmware }));
  // The list connection has no channel field. Resolve its promotion associations
  // in bounded batches so large group catalogues do not flood the gateway.
  for (let offset = 0; includePromotions && offset < items.length; offset += 4) {
    const promoted = await Promise.all(items.slice(offset, offset + 4).map((firmware) => withFirmwarePromotions(idToken, firmware)));
    items.splice(offset, promoted.length, ...promoted);
  }
  return {
    items,
    nextToken: connection?.nextToken || null,
  };
}

export async function getFirmware(idToken, { id }) {
  const query = `query GetFirmware($id: ID!) {
    getFirmware(id: $id) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmware {
        ${FIRMWARE_LIST_FIELDS}
        promotions { id firmwareId toChannel createdAt promotedBy }
        releaseNotes { ${RELEASE_NOTE_FIELDS} }
      }
    }
  }`;
  const data = await graphqlRequest(query, { id }, idToken);
  const result = firmwareManagementResult(data?.getFirmware, 'Firmware details');
  const firmware = Array.isArray(result.firmware) ? result.firmware.find((item) => item?.id === id) : null;
  if (!firmware) throw new Error('Firmware package was not found. Refresh the catalogue and retry.');
  return {
    ...firmware,
    region: result.region,
    releaseNotes: (firmware.releaseNotes || []).filter(Boolean),
    promotions: (firmware.promotions || []).filter(Boolean),
    sbomKeys: (firmware.sbomKeys || []).filter(Boolean),
  };
}

export async function listAvailabilityGroups(idToken, { limit = 100, nextToken = '' } = {}) {
  const query = `query ListAvailabilityGroups($limit: Int, $nextToken: String) {
    availabilityGroups(limit: $limit, nextToken: $nextToken) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group {
        items { ${AVAILABILITY_GROUP_FIELDS} }
        nextToken
      }
    }
  }`;

  const data = await graphqlRequest(query, { limit, nextToken }, idToken);
  const result = firmwareManagementResult(data?.availabilityGroups, 'Availability group list');
  const connection = result.group;

  return {
    items: Array.isArray(connection?.items) ? connection.items : [],
    nextToken: connection?.nextToken || null,
  };
}

export async function findUserByEmail(idToken, { email, regionId = 'ALL' }) {
  const query = `query FindUserByEmail($email: AWSEmail!, $regionId: RegionName!) {
    findUserByEmail(email: $email, regionId: $regionId) {
      region
      errorInfo { ${ERROR_INFO_FIELDS} }
      userData { email userId }
    }
  }`;
  const data = await graphqlRequest(query, { email: email.trim(), regionId }, idToken);
  if (!Array.isArray(data?.findUserByEmail) || !data.findUserByEmail.length) {
    throw new Error('No customer lookup response was returned.');
  }
  return data.findUserByEmail;
}

export async function listPromotions(idToken, { firmwareId }) {
  const query = `query ListPromotions($firmwareId: ID!) {
    listPromotions(firmwareId: $firmwareId) {
      region
      errorInfo { ${ERROR_INFO_FIELDS} }
      promotion { id firmwareId toChannel createdAt promotedBy }
    }
  }`;
  const data = await graphqlRequest(query, { firmwareId }, idToken);
  const result = firmwareManagementResult(data?.listPromotions, 'Firmware promotion history');
  const promotions = Array.isArray(result.promotion) ? result.promotion.filter(Boolean) : [];
  if (promotions.some((promotion) => promotion.firmwareId !== firmwareId)) {
    throw new Error('The promotion API returned records for a different firmware package.');
  }
  return promotions;
}

export async function promoteFirmware(idToken, { firmwareId, toChannel, promotedBy }) {
  if (!firmwareId || !FIRMWARE_CHANNELS.includes(toChannel)) {
    throw new Error('Select a firmware package and a supported promotion channel.');
  }
  const mutation = `mutation PromoteFirmware($input: PromoteFirmwareInput!) {
    promoteFirmware(input: $input) {
      region
      errorInfo { ${ERROR_INFO_FIELDS} }
      promotion { id firmwareId toChannel createdAt promotedBy }
    }
  }`;
  const input = { firmwareId, toChannel };
  if (promotedBy) input.promotedBy = promotedBy;
  const data = await graphqlRequest(mutation, { input }, idToken);
  const result = firmwareManagementResult(data?.promoteFirmware, 'Firmware promotion');
  if (!result.promotion?.id || !result.promotion.createdAt
    || result.promotion.firmwareId !== firmwareId || result.promotion.toChannel !== toChannel) {
    throw new Error('The API did not confirm the requested firmware promotion. Refresh before retrying.');
  }
  return { ...result.promotion, region: result.region };
}

export async function getAvailabilityGroup(idToken, { groupId }) {
  const query = `query GetAvailabilityGroup($groupId: String!) {
    availabilityGroup(groupId: $groupId) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group { ${AVAILABILITY_GROUP_DETAIL_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { groupId }, idToken);
  const result = firmwareManagementResult(data?.availabilityGroup, 'Availability group lookup');
  return result.group || null;
}

export async function createAvailabilityGroup(idToken, { groupName, groupType }) {
  const mutation = `mutation CreateAvailabilityGroup($input: CreateAvailabilityGroupInput!) {
    createAvailabilityGroup(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group { ${AVAILABILITY_GROUP_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, { input: { groupName, groupType } }, idToken);
  const result = firmwareManagementResult(data?.createAvailabilityGroup, 'Availability group creation');
  if (!result.group) {
    throw new Error('Availability group creation completed without a group record.');
  }
  return { region: result.region, ...result.group };
}

export async function updateAvailabilityGroup(idToken, { groupId, groupName, groupType }) {
  const mutation = `mutation UpdateAvailabilityGroup($input: UpdateAvailabilityGroupInput!) {
    updateAvailabilityGroup(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group { ${AVAILABILITY_GROUP_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, { input: { groupId, groupName, groupType } }, idToken);
  const result = firmwareManagementResult(data?.updateAvailabilityGroup, 'Availability group update');
  if (!result.group) {
    throw new Error('Availability group update completed without a group record.');
  }
  return { region: result.region, ...result.group };
}

export async function deleteAvailabilityGroup(idToken, { groupId }) {
  if (!groupId?.trim()) throw new Error('Select a firmware group to remove.');
  const mutation = `mutation DeleteAvailabilityGroup($input: DeleteAvailabilityGroupInput!) {
    deleteAvailabilityGroup(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group { id }
    }
  }`;

  const data = await graphqlRequest(mutation, { input: { groupId } }, idToken);
  const result = firmwareManagementResult(data?.deleteAvailabilityGroup, 'Availability group deletion');
  if (result.group?.id !== groupId) {
    throw new Error('The API did not confirm removal of this firmware group. Refresh before retrying.');
  }
  return { region: result.region, ...result.group };
}

export function normalizeUserRegion(value) {
  const region = String(value || '').trim().toUpperCase().replace(/-/g, '_');
  return ['ALL', ...FIRMWARE_REGIONS].includes(region) ? region : '';
}

async function updateAvailabilityGroupMembership(idToken, { operation, groupId, firmwareId, groupUserId, userRegion }) {
  const isFirmware = Boolean(firmwareId);
  const fieldName = operation === 'add' ? (isFirmware ? 'addFirmwareToAvailabilityGroup' : 'addUserToAvailabilityGroup') : (isFirmware ? 'deleteFirmwareFromAvailabilityGroup' : 'deleteUserFromAvailabilityGroup');
  const inputType = operation === 'add'
    ? (isFirmware ? 'AddFirmwareToAvailabilityGroupInput' : 'AddUserToAvailabilityGroupInput')
    : (isFirmware ? 'DeleteFirmwareFromAvailabilityGroupInput' : 'DeleteUserFromAvailabilityGroupInput');
  const input = isFirmware ? { groupId, firmwareId } : { groupId, groupUserId };
  if (!isFirmware && operation === 'add') {
    input.userRegion = normalizeUserRegion(userRegion);
    if (!input.userRegion) throw new Error('A supported customer region is required.');
  }
  const groupFields = isFirmware ? 'id firmwareId createdAt updatedAt' : 'id userId userRegion createdAt updatedAt';
  const mutation = `mutation UpdateAvailabilityGroupMembership($input: ${inputType}!) {
    ${fieldName}(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      group { ${groupFields} }
    }
  }`;

  const data = await graphqlRequest(mutation, { input }, idToken);
  const result = firmwareManagementResult(data?.[fieldName], 'Availability group membership update');
  if (operation === 'delete' && !isFirmware && result.group === null) {
    // A successful delete can omit its receipt. Verify membership instead of rejecting it.
    const group = await getAvailabilityGroup(idToken, { groupId });
    const members = Array.isArray(group?.userList) ? group.userList.filter(Boolean) : null;
    if (group?.id === groupId && members
      && members.every((member) => typeof member.userId === 'string' && member.userId)
      && !members.some((member) => member.userId === groupUserId)) {
      return { region: result.region, userId: groupUserId };
    }
    throw new Error('Customer removal could not be confirmed. Refresh the group before retrying.');
  }
  if (!result.group || (isFirmware ? result.group.firmwareId !== firmwareId : result.group.userId !== groupUserId)) {
    throw new Error('The API did not confirm the requested group membership change. Refresh before retrying.');
  }
  if (!isFirmware && operation === 'add' && normalizeUserRegion(result.group.userRegion) !== input.userRegion) {
    throw new Error('The API did not confirm the requested customer region. Refresh the group before retrying.');
  }
  return { region: result.region, ...result.group };
}

export function addFirmwareToAvailabilityGroup(idToken, { groupId, firmwareId }) {
  return updateAvailabilityGroupMembership(idToken, { operation: 'add', groupId, firmwareId });
}

export function deleteFirmwareFromAvailabilityGroup(idToken, { groupId, firmwareId }) {
  return updateAvailabilityGroupMembership(idToken, { operation: 'delete', groupId, firmwareId });
}

export function addUserToAvailabilityGroup(idToken, { groupId, groupUserId, userRegion }) {
  return updateAvailabilityGroupMembership(idToken, { operation: 'add', groupId, groupUserId, userRegion });
}

export function deleteUserFromAvailabilityGroup(idToken, { groupId, groupUserId }) {
  return updateAvailabilityGroupMembership(idToken, { operation: 'delete', groupId, groupUserId });
}

export async function getReleaseNote(idToken, { firmwareId, language }) {
  const query = `query GetReleaseNote($firmwareId: ID!, $language: LanguageCode!) {
    getReleaseNote(firmwareId: $firmwareId, language: $language) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      releaseNote { ${RELEASE_NOTE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { firmwareId, language }, idToken);
  const result = firmwareManagementResult(data?.getReleaseNote, 'Release note lookup');
  return result.releaseNote || null;
}

export async function listReleaseNotes(idToken, { firmwareId }) {
  const query = `query ListReleaseNotes($firmwareId: ID!) {
    listReleaseNotes(firmwareId: $firmwareId) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      releaseNote { ${RELEASE_NOTE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { firmwareId }, idToken);
  const result = firmwareManagementResult(data?.listReleaseNotes, 'Release note list');
  return Array.isArray(result.releaseNote) ? result.releaseNote : [];
}

export async function upsertReleaseNote(idToken, { firmwareId, language, title, body }) {
  const mutation = `mutation UpsertReleaseNote($input: UpsertReleaseNoteInput!) {
    upsertReleaseNote(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      releaseNote { ${RELEASE_NOTE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, {
    input: { firmwareId, language, title: title || null, body },
  }, idToken);
  const result = firmwareManagementResult(data?.upsertReleaseNote, 'Release note save');
  if (!result.releaseNote) {
    throw new Error('Release note save completed without a release note record.');
  }
  return { region: result.region, ...result.releaseNote };
}

export async function deleteReleaseNote(idToken, { firmwareId, language }) {
  const mutation = `mutation DeleteReleaseNote($input: DeleteReleaseNoteInput!) {
    deleteReleaseNote(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      releaseNote { ${RELEASE_NOTE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, {
    input: { firmwareId, language },
  }, idToken);
  const result = firmwareManagementResult(data?.deleteReleaseNote, 'Release note deletion');
  return result.releaseNote ? { region: result.region, ...result.releaseNote } : null;
}

export async function getChangeLog(idToken, { firmwareId, language }) {
  const query = `query GetChangeLog($firmwareId: ID!, $language: LanguageCode!) {
    getChangeLog(firmwareId: $firmwareId, language: $language) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      changeLog { ${CHANGE_LOG_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { firmwareId, language }, idToken);
  const result = firmwareManagementResult(data?.getChangeLog, 'Changelog lookup');
  return result.changeLog || null;
}

export async function listChangeLog(idToken, { firmwareId }) {
  const query = `query ListChangeLog($firmwareId: ID!) {
    listChangeLog(firmwareId: $firmwareId) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      changeLog { ${CHANGE_LOG_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { firmwareId }, idToken);
  const result = firmwareManagementResult(data?.listChangeLog, 'Changelog list');
  return Array.isArray(result.changeLog) ? result.changeLog : [];
}

export async function upsertChangeLog(idToken, { firmwareId, language, title, body }) {
  const mutation = `mutation UpsertChangeLog($input: UpsertChangeLogInput!) {
    upsertChangeLog(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      changeLog { ${CHANGE_LOG_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, {
    input: { firmwareId, language, title: title || null, body },
  }, idToken);
  const result = firmwareManagementResult(data?.upsertChangeLog, 'Changelog save');
  if (!result.changeLog) {
    throw new Error('Changelog save completed without a changelog record.');
  }
  return { region: result.region, ...result.changeLog };
}

export async function deleteChangeLog(idToken, { firmwareId, language }) {
  const mutation = `mutation DeleteChangeLog($input: DeleteChangeLogInput!) {
    deleteChangeLog(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      changeLog { ${CHANGE_LOG_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, {
    input: { firmwareId, language },
  }, idToken);
  const result = firmwareManagementResult(data?.deleteChangeLog, 'Changelog deletion');
  return result.changeLog ? { region: result.region, ...result.changeLog } : null;
}

export async function getDeviceDataById(idToken, { deviceId, childDeviceId, regionId }) {
  const query = `query GetDeviceDataById($deviceId: ID!, $childDeviceId: String!, $regionId: RegionName!) {
    deviceDataById(deviceId: $deviceId, childDeviceId: $childDeviceId, regionId: $regionId) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      deviceData { ${DEVICE_SHADOW_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(query, { deviceId, childDeviceId, regionId }, idToken);
  const results = Array.isArray(data?.deviceDataById) ? data.deviceDataById : [];
  const regionalResults = results.filter((item) => item && (regionId === 'ALL'
    || String(item.region || '').toUpperCase().replace(/-/g, '_') === regionId));
  // ALL returns one result per region; a miss in one region must not hide a device in another.
  const result = regionalResults.find((item) => item.deviceData && !item.errorInfo)
    || regionalResults.find((item) => item.errorInfo && item.errorInfo.error !== 'DEVICE_NOT_FOUND')
    || regionalResults.find((item) => item.errorInfo)
    || regionalResults[0];
  if (!result) {
    throw new Error(`No Device lookup response was returned for ${regionId}.`);
  }
  throwRegionalError(result, 'Device lookup');
  if (!result.deviceData) {
    throw new Error('No device data was returned for the supplied device identifiers.');
  }
  return result;
}

export async function deleteFirmware(idToken, { deviceModel, hardwareVersion, firmwareVersion }) {
  const mutation = `mutation DeleteFirmware($input: DeleteFirmwareInput!) {
    deleteFirmware(input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmware { ${FIRMWARE_FIELDS} }
    }
  }`;
  const data = await graphqlRequest(mutation, {
    input: { deviceModel, hardwareVersion, firmwareVersion },
  }, idToken);
  const deletionResults = Array.isArray(data?.deleteFirmware) ? data.deleteFirmware : [];
  const blockingError = deletionResults.find((result) => result?.errorInfo?.error !== 'NO_DATA_FOUND' && result?.errorInfo);
  if (blockingError) {
    throwRegionalError(blockingError, 'Firmware deletion');
  }
  const deletedFirmware = deletionResults.flatMap((result) => (
    result?.firmware ? [{ region: result.region, ...result.firmware }] : []
  ));
  if (!deletedFirmware.length) {
    const notFoundResult = deletionResults.find((result) => result?.errorInfo?.error === 'NO_DATA_FOUND');
    if (notFoundResult) {
      throwRegionalError(notFoundResult, 'Firmware deletion');
    }
    throw new Error('Firmware package was not found.');
  }
  return deletedFirmware;
}

export async function requestFirmwareUpgrade(idToken, { regionId, hardwareVersion, currentVersion, channel, deviceId, childDeviceId }) {
  const mutation = `mutation RequestFirmwareUpgrade($regionId: RegionName!, $input: FirmwareUpgradeRequestInput!) {
    requestUpgradeFirmware(regionId: $regionId, input: $input) {
      errorInfo { ${ERROR_INFO_FIELDS} }
      region
      firmwareData { ${AVAILABLE_FIRMWARE_FIELDS} }
    }
  }`;

  const data = await graphqlRequest(mutation, {
    regionId,
    input: {
      hardwareVersion,
      currentVersion,
      channel,
      deviceId,
      childDeviceId: childDeviceId || null,
    },
  }, idToken);
  return availableFirmwareResults(data?.requestUpgradeFirmware, 'Firmware update request');
}
